import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import AssigneePicker from './AssigneePicker';
import WorkNotes from './WorkNotes';
import { useAccess } from '../lib/access';

export default function TaskModal({ task, properties, existingPhotos = [], defaultPropertyId, onSaved, onClose }) {
  const access = useAccess();
  const [title, setTitle] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [assignedTo, setAssignedTo] = useState(null);
  const [priority, setPriority] = useState('Medium');
  const [dueDate, setDueDate] = useState('');
  const [vendor, setVendor] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceInterval, setRecurrenceInterval] = useState('Monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [photosToKeep, setPhotosToKeep] = useState(existingPhotos);

  useEffect(() => {
    if (task) {
      setTitle(task.title || '');
      setPropertyId(task.property_id || '');
      setAssignedTo(task.assigned_to || null);
      setPriority(task.priority || 'Medium');
      setDueDate(task.due_date || '');
      setVendor(task.vendor || '');
      setCost(task.cost || '');
      setNotes(task.notes || '');
      setIsRecurring(task.is_recurring || false);
      setRecurrenceInterval(task.recurrence_interval || 'Monthly');
    } else {
      if (defaultPropertyId) {
        setPropertyId(defaultPropertyId);
      } else if (properties.length === 1) {
        setPropertyId(properties[0].id);
      }
    }
  }, [task, properties, defaultPropertyId]);

  const handleFileChange = (e) => {
    setPendingFiles(prev => [...prev, ...Array.from(e.target.files)]);
    e.target.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingPhoto = (photoId) => {
    setPhotosToKeep(prev => prev.filter(p => p.id !== photoId));
  };

  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required.'); return; }
    // A task with no property is only allowed for all-properties roles (the database refuses others).
    if (!propertyId && !access.can(null, 'edit')) { setError('Choose a property.'); return; }
    setLoading(true);
    setError('');

    const payload = {
      title: title.trim(),
      property_id: propertyId || null,
      assigned_to: assignedTo || null,
      priority,
      due_date: dueDate || null,
      vendor: vendor.trim() || null,
      cost: cost ? parseFloat(cost) : 0,
      notes: notes.trim() || null,
      is_recurring: isRecurring,
      recurrence_interval: isRecurring ? recurrenceInterval : null,
      updated_at: new Date().toISOString(),
    };

    let savedTask;
    if (task) {
      const { data, error } = await supabase
        .from('tasks')
        .update(payload)
        .eq('id', task.id)
        .select('*, properties(name)')
        .single();
      if (error) { setError(error.message); setLoading(false); return; }
      savedTask = data;
    } else {
      const { data, error } = await supabase
        .from('tasks')
        .insert({ ...payload, done: false })
        .select('*, properties(name)')
        .single();
      if (error) { setError(error.message); setLoading(false); return; }
      savedTask = data;
    }

    // Delete removed photos
    if (task) {
      const removedPhotos = existingPhotos.filter(
        ep => !photosToKeep.find(k => k.id === ep.id)
      );
      for (const photo of removedPhotos) {
        await supabase.storage.from('task-photos').remove([photo.storage_path]);
        await supabase.from('task_photos').delete().eq('id', photo.id);
      }
    }

    // Upload new photos
    for (const file of pendingFiles) {
      const path = `${savedTask.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('task-photos')
        .upload(path, file);
      if (!uploadError) {
        await supabase.from('task_photos').insert({
          task_id: savedTask.id,
          storage_path: path,
          filename: file.name,
        });
      }
    }

    setLoading(false);
    onSaved(savedTask, !task);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{task ? 'Edit Task' : 'Add Task'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-msg">{error}</div>}

          <div className="form-group">
            <label>Task *</label>
            <input
              autoFocus
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Fix broken screen door"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Property</label>
              <select value={propertyId} onChange={e => setPropertyId(e.target.value)}>
                <option value="">-- Select --</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{isRecurring ? 'First Due Date' : 'Due Date'}</label>
              <input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Est. Cost ($)</label>
              <input
                type="number"
                value={cost}
                onChange={e => setCost(e.target.value)}
                placeholder="0"
                min="0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Recurring Task</label>
              <div
                className={`toggle-switch ${isRecurring ? 'on' : ''}`}
                onClick={() => setIsRecurring(!isRecurring)}
              >
                <div className="toggle-knob" />
              </div>
            </div>

            {isRecurring && (
              <div className="form-group">
                <label>Repeat Every</label>
                <select value={recurrenceInterval} onChange={e => setRecurrenceInterval(e.target.value)}>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Annually">Annually</option>
                </select>
              </div>
            )}
          </div>

          <AssigneePicker propertyId={propertyId} value={assignedTo} onChange={setAssignedTo} />

          <div className="form-group">
            <label>Vendor</label>
            <input
              value={vendor}
              onChange={e => setVendor(e.target.value)}
              placeholder="e.g. Mike's Plumbing, Cleaning crew"
            />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any additional details..."
            />
          </div>

          {task && <WorkNotes taskId={task.id} authorEmail={access.email} />}

          <div className="form-group">
            <label>Photos</label>

            {(photosToKeep.length > 0 || pendingFiles.length > 0) && (
              <div className="photo-thumbnails">
                {photosToKeep.map(photo => (
                  <div key={photo.id} className="photo-thumb">
                    <img src={photo.url} alt={photo.filename || 'Photo'} />
                    <button
                      type="button"
                      className="photo-remove"
                      onClick={() => removeExistingPhoto(photo.id)}
                    >&times;</button>
                  </div>
                ))}
                {pendingFiles.map((file, i) => (
                  <div key={`pending-${i}`} className="photo-thumb">
                    <img src={URL.createObjectURL(file)} alt={file.name} />
                    <button
                      type="button"
                      className="photo-remove"
                      onClick={() => removePendingFile(i)}
                    >&times;</button>
                  </div>
                ))}
              </div>
            )}

            <label className="photo-upload-area">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <span>+ Add Photos</span>
            </label>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : task ? 'Save Changes' : 'Add Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
